import { type CompressResult, getMediaKind, type MediaKind } from './compressMedia';

export type RowStatus = 'queued' | 'compressing' | 'naming' | 'ready' | 'over_limit' | 'error';

export interface UploadRow {
  clientId: string;
  originalFile: File;
  uploadFile: File | null;
  namingFile: File | null;
  originalSize: number;
  finalSize: number | null;
  originalName: string;
  suggestedName: string | null;
  editedName: string | null;
  finalName: string;
  kind: MediaKind;
  status: RowStatus;
  errorMessage?: string;
  thumbnailUrl?: string;
}

const sanitizeBaseName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_]/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(lastDot + 1).toLowerCase() : '';
};

export const getFileBaseName = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
};

const stripMatchingExtension = (name: string, extension: string): string => {
  if (!extension) return name;
  const suffix = `.${extension}`;
  return name.toLowerCase().endsWith(suffix) ? name.slice(0, -suffix.length) : name;
};

const buildNameWithOriginalExtension = (name: string, originalName: string): string => {
  const extension = getFileExtension(originalName);
  const baseName = stripMatchingExtension(name.trim(), extension);
  const sanitizedBaseName = sanitizeBaseName(baseName) || sanitizeBaseName(getFileBaseName(originalName));
  return extension ? `${sanitizedBaseName}.${extension}` : sanitizedBaseName;
};

export const normalizeEditedName = (name: string, originalName: string): string => {
  const extension = getFileExtension(originalName);
  const baseName = stripMatchingExtension(name.trim(), extension);
  return sanitizeBaseName(baseName) || sanitizeBaseName(getFileBaseName(originalName));
};

export const buildFinalName = (row: Pick<UploadRow, 'editedName' | 'suggestedName' | 'originalName'>): string => {
  if (row.editedName) return buildNameWithOriginalExtension(row.editedName, row.originalName);
  return row.suggestedName ?? buildNameWithOriginalExtension(row.originalName, row.originalName);
};

export const createInitialRow = (file: File): UploadRow => {
  const kind = getMediaKind(file);
  const thumbnailUrl = kind === 'image' ? URL.createObjectURL(file) : undefined;
  return {
    clientId: crypto.randomUUID(),
    originalFile: file,
    uploadFile: null,
    namingFile: null,
    originalSize: file.size,
    finalSize: null,
    originalName: file.name,
    suggestedName: null,
    editedName: null,
    finalName: buildNameWithOriginalExtension(file.name, file.name),
    kind,
    status: 'queued',
    thumbnailUrl,
  };
};

export const updateRowById = (rows: UploadRow[], clientId: string, update: Partial<UploadRow>): UploadRow[] =>
  rows.map((r) => {
    if (r.clientId !== clientId) return r;
    const normalizedUpdate =
      'editedName' in update
        ? { ...update, editedName: update.editedName ? normalizeEditedName(update.editedName, r.originalName) : null }
        : update;
    const merged = { ...r, ...normalizedUpdate };
    return { ...merged, finalName: buildFinalName(merged) };
  });

export const applyCompressResult = (row: UploadRow, result: CompressResult): Partial<UploadRow> => {
  if (!result.withinLimit) {
    const messages: Record<string, string> = {
      VIDEO_TOO_LARGE: 'Video supera 16 MB. Comprime el video antes de subir.',
      PDF_TOO_LARGE: 'Archivo supera 100 MB.',
      IMAGE_STILL_TOO_LARGE: 'Imagen demasiado grande, incluso comprimida.',
      GIF_TOO_LARGE: 'GIF supera 16 MB.',
    };
    return {
      uploadFile: result.uploadFile,
      namingFile: result.namingFile,
      finalSize: result.finalSize,
      status: 'over_limit',
      errorMessage: messages[result.reason ?? ''] ?? 'Archivo demasiado grande.',
    };
  }

  const needsNaming = row.kind === 'image' && row.originalFile.type !== 'image/gif';
  return {
    uploadFile: result.uploadFile,
    namingFile: result.namingFile,
    finalSize: result.finalSize,
    status: needsNaming ? 'naming' : 'ready',
  };
};

export const hasDuplicateNames = (rows: UploadRow[]): boolean => {
  const readyNames = rows.filter((r) => r.status === 'ready').map((r) => r.finalName);
  return new Set(readyNames).size < readyNames.length;
};

export const revokeRowThumbnails = (rows: UploadRow[]): void => {
  rows.forEach((r) => {
    if (r.thumbnailUrl) URL.revokeObjectURL(r.thumbnailUrl);
  });
};
